import React, { useState } from 'react';
import {
    Box,
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    useTheme,
    useMediaQuery,
} from '@material-ui/core';
import { makeStyles, fade } from '@material-ui/core/styles';
import { HelpOutline } from "components/MaterialSymbol/MaterialIcons";
import { Close as CloseIcon } from "components/MaterialSymbol/MaterialIcons";

const useStyles = makeStyles((theme) => ({
    iconButton: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '36px',
        height: '36px',
        padding: 0,
        color: '#ffffff',
        borderRadius: '999px',
        boxShadow: 'none',
        background: '#111827',
        border: 'none',
        transition: 'background-color 0.2s ease, transform 0.2s ease',
        '&:hover': {
            background: '#1F2937',
            transform: 'scale(1.04)',
        }
    },
    dialogTitle: {
        margin: 0,
        padding: theme.spacing(2.5),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        backgroundColor: fade(theme.palette.primary.main, 0.02),
        borderBottom: `1px solid ${theme.palette.divider}`,
    },
    dialogTitleBox: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.5),
    },
    closeButton: {
        color: theme.palette.grey[500],
        '&:hover': { color: theme.palette.error.main },
    },
    dialogActions: {
        padding: theme.spacing(2.5),
        backgroundColor: fade(theme.palette.primary.main, 0.02),
        borderTop: `1px solid ${theme.palette.divider}`,
    },
    quickOverviewBox: {
        padding: theme.spacing(2.5),
        backgroundColor: fade(theme.palette.primary.main, 0.04),
        borderRadius: theme.shape.borderRadius,
        borderLeft: `4px solid ${theme.palette.primary.main}`,
        marginBottom: theme.spacing(4),
    },
}));

export const StepHelp = ({ title, description, content }) => {
    const [open, setOpen] = useState(false);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const classes = useStyles();

    const handleOpen = (e) => {
        e.stopPropagation();
        setOpen(true);
    };

    const handleClose = (e) => {
        if (e) e.stopPropagation();
        setOpen(false);
    };

    return (
        <React.Fragment>
            <Tooltip title="Need help with this step?" arrow>
                <IconButton onClick={handleOpen} size="small" className={classes.iconButton}>
                    <HelpOutline style={{ fontSize: '1rem', color: '#ffffff' }} />
                </IconButton>
            </Tooltip>

            <Dialog
                id="step-help-dialog"
                open={open}
                onClose={handleClose}
                fullScreen={isMobile}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    style: {
                        borderRadius: isMobile ? 0 : 12,
                        overflow: 'hidden'
                    }
                }}
            >
                <DialogTitle className={classes.dialogTitle} disableTypography>
                    <Box className={classes.dialogTitleBox}>
                        <HelpOutline style={{ color: '#111827' }} />
                        <Typography variant="h6" component="div" style={{ fontWeight: 700 }}>
                            {title} Help
                        </Typography>
                    </Box>
                    <IconButton aria-label="close" onClick={handleClose} className={classes.closeButton}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent style={{ padding: theme.spacing(4) }}>
                    {description && (
                        <Typography variant="body1" color="textSecondary" paragraph style={{ lineHeight: 1.7 }}>
                            {description}
                        </Typography>
                    )}

                    {content || (
                        <Box style={{ paddingTop: theme.spacing(1), paddingBottom: theme.spacing(1) }}>
                            <Typography variant="body1" paragraph style={{ lineHeight: 1.7, marginBottom: theme.spacing(3) }}>
                                This section allows you to configure specific settings for this step.
                                Proper configuration ensures that your SaaS platform operates according to your business requirements.
                            </Typography>

                            <Box className={classes.quickOverviewBox}>
                                <Typography variant="subtitle2" style={{ fontWeight: 700 }} gutterBottom color="primary">
                                    Quick Overview
                                </Typography>
                                <Typography variant="body2" style={{ lineHeight: 1.6, color: theme.palette.text.secondary }}>
                                    Follow the instructions on the screen to provide the necessary information.
                                    If you're unsure about a specific field, you can usually leave it as default or consult the documentation.
                                </Typography>
                            </Box>

                            <Box style={{ marginTop: theme.spacing(4) }}>
                                <Typography variant="subtitle2" style={{ fontWeight: 700 }} gutterBottom>
                                    Need further assistance?
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                    Detailed documentation will be available soon. For immediate support, please contact your account manager.
                                </Typography>
                            </Box>
                        </Box>
                    )}
                </DialogContent>

                <DialogActions className={classes.dialogActions}>
                    <Button
                        onClick={handleClose}
                        variant="contained"
                        size="large"
                        color="primary"
                        style={{
                            borderRadius: 8,
                            paddingLeft: theme.spacing(4),
                            paddingRight: theme.spacing(4),
                            boxShadow: `0 4px 12px ${fade(theme.palette.primary.main, 0.2)}`
                        }}
                    >
                        Got it, thanks!
                    </Button>
                </DialogActions>
            </Dialog>
        </React.Fragment>
    );
};
